import React, { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LiveKitCallOverlay from './LiveKitCallOverlay';

/**
 * Full-page Direct Call (opened in its own browser tab so chat stays usable).
 */
const LiveKitCallPage = () => {
  const { isAuthenticated, token } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  const channelId = searchParams.get('channelId') || '';
  const messageId = searchParams.get('messageId') || '';
  const roomName = searchParams.get('roomName') || '';
  const title = searchParams.get('title') || 'Direct call';

  useEffect(() => {
    const nextTitle = `${title} · WorkLoop`;
    const prev = document.title;
    document.title = nextTitle;
    try {
      window.workloopRealm?.setWindowTitle?.(nextTitle);
    } catch {
      // Browser / preload without the helper
    }
    return () => {
      document.title = prev;
    };
  }, [title]);

  const handleLeave = useCallback(() => {
    try {
      if (window.workloopRealm?.closeCallWindow) {
        window.workloopRealm.closeCallWindow();
        return;
      }
    } catch {
      // fall through
    }
    window.close();
  }, []);

  const missingParams = useMemo(
    () => !channelId || (!messageId && !roomName),
    [channelId, messageId, roomName],
  );

  if (!isAuthenticated && !token) {
    return <Navigate to="/login" replace />;
  }

  if (missingParams) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-950 text-white px-6 text-center">
        <p className="text-sm text-red-300">This call link is incomplete.</p>
        <button
          type="button"
          onClick={handleLeave}
          className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-white/10 hover:bg-white/20"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <LiveKitCallOverlay
      channelId={channelId}
      messageId={messageId || undefined}
      roomName={roomName || undefined}
      title={title}
      onLeave={handleLeave}
    />
  );
};

export default LiveKitCallPage;
